import { TextInput } from "@somethings-ui/web";
import { Loader2, Search } from "lucide-react";
import { Accordion } from "@/components/ui/accordion";
import { useTrainingSimulatorReviewHome } from "@/lib/api/training-simulator/training-simulator-review";
import {
  PostHogEvents,
  useTrackEvent,
  useTrackPage,
} from "@/lib/posthog-events";
import { ReviewCategory } from "./review-category";
import { ReviewBatchApproveBar } from "./review-batch-approve-bar";
import { useMemo, useState } from "react";
import { useDebounce } from "@/lib/hooks/use-debounce";

export function TrainingReviewHome() {
  useTrackPage(PostHogEvents.PAGE_TRAINING_REVIEW);
  const { track } = useTrackEvent();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data, isLoading, isError } =
    useTrainingSimulatorReviewHome(debouncedSearchTerm);

  const toggleSelect = (progressId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(progressId)) {
        next.delete(progressId);
      } else {
        next.add(progressId);
      }
      return next;
    });
  };

  // Only ids still in the "needs review" bucket are actionable, so the bar count
  // and the approve payload stay correct after search filtering or a refetch.
  const selectedNeedsReview = useMemo(
    () =>
      (data?.needs_review ?? [])
        .map((row) => row.progressId)
        .filter((id) => selectedIds.has(id)),
    [data?.needs_review, selectedIds],
  );

  return (
    <div className="min-h-full bg-surfaces-z3 p-8">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h1 className="text-header-2 text-text-primary">Mentor Reviews</h1>
          <p className="text-body-3 text-text-secondary">
            {data?.counts?.in_training || 0} trainees ·{" "}
            {data?.counts?.awaiting_review || 0} awaiting your review
          </p>
        </div>

        <TextInput
          className="w-[323px] max-w-full"
          leadingIcon={<Search className="size-5 text-text-tertiary" />}
          placeholder="Search trainees by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {isLoading ? (
          <div className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3">
            <Loader2 className="size-10 animate-spin text-text-secondary" />
          </div>
        ) : isError || !data ? (
          <div className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3">
            <p className="text-body-1 text-text-secondary">
              Something went wrong loading your training. Please try again.
            </p>
          </div>
        ) : (
          <Accordion
            type="multiple"
            defaultValue={["Needs review", "In progress"]}
            className="flex flex-col gap-3"
            onValueChange={(openSections) =>
              track(PostHogEvents.SIMULATOR_LM_SECTION_TOGGLE, {
                open_sections: openSections,
              })
            }
          >
            <ReviewCategory
              count={data.needs_review.length}
              items={data.needs_review}
              title="Needs review"
              type="needs_review"
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
            <ReviewCategory
              count={data.in_progress.length}
              items={data.in_progress}
              title="In progress"
              type="in_progress"
            />
            <ReviewCategory
              count={data.approved.length}
              items={data.approved}
              title="Approved"
              type="approved"
            />
          </Accordion>
        )}
      </div>

      <ReviewBatchApproveBar
        progressIds={selectedNeedsReview}
        onApproved={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
